import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import "dayjs/locale/es";
import {
	getClubAvailabilityApi,
	holdClubReservationApi,
	createClubReservationApi,
	createOpenMatchApi,
	getAppSettings,
} from "../../api/api";
import {
	ALLOWED_DURATIONS,
	MAX_PLAYERS_PER_RESERVATION,
	resolveDefaultPlayers,
} from "../../constants/courtDomain";
import { FISERV_CARD_OPTIONS } from "../../constants/fiservPaymentOptions";
import { resolveUrl, COUNTRIES } from "../reservas/components/ReservaShared";
import { slotRequiresOnlinePayment } from "./courtDeposit";
import { pruneUnpricedStarts, durationsWithPricing } from "./courtDurations";
import { buildCreatePayload, classifyCreateError, saveManageToken } from "./openMatchesModel";
import { waitlistPrefill, waitlistTokenFromSearch } from "../listaDeEspera/waitlistModel";

dayjs.locale("es");

// Court-namespaced sessionStorage keys (ADR-3, design.md sec.4/9). These MUST
// NEVER collide with the bar flow's `reservation:hold_state` /
// `reservation:pending_selection` keys (see `useReservationFlow.js`) — a
// court hold and a bar hold must be able to coexist across tabs/logins.
const COURT_HOLD_STATE_KEY = "court_reservation:hold_state";
const COURT_PENDING_SELECTION_KEY = "court_reservation:pending_selection";

/**
 * Court reservation flow — buyer side.
 *
 * PASS 1 (Unit 3a): availability fetch + slot selection.
 *   - Step 1 context: date / sport / duration selection (mirrors bar's
 *     step 1, replacing `partySize` with `selectedDuration`/`selectedSport`
 *     per design.md sec.4).
 *   - Step 2: fetch the availability grid ONCE per date (ADR-6 — omit
 *     `durationMinutes`/`sport` from the request; the backend already
 *     returns all `allowedDurations` per court, so we re-filter
 *     client-side on duration/sport change and refetch only on date
 *     change). Selectable starts are derived straight from the
 *     precomputed `bookableStarts[duration]` map returned by the backend
 *     — this hook never re-derives interval overlap itself.
 *   - Slot selection state (`selectedCourtId` / `selectedStart`).
 *
 * PASS 2 (Unit 3b, THIS UPDATE) adds:
 *   - Hold: `handleAdvanceToForm()` -> `holdClubReservationApi({clubId,
 *     courtId, date, slotStart, durationMinutes}, token)`. Named to match
 *     the bar hook's `handleAdvanceToForm` 1:1 (fidelity target) even
 *     though the court module doesn't have a data-entry form step yet —
 *     Unit 3c wires the form/checkout behind this same handoff point.
 *   - Auth gate: no token -> persist the pending selection under
 *     `court_reservation:pending_selection` and redirect to `/login`
 *     (mirrors bar's `handleAdvanceToForm`, NEVER touches the bar's
 *     `reservation:pending_selection` key).
 *   - Hold countdown: exposes `holdId`/`holdExpiresAt`; callers render the
 *     shared `HoldCountdown` (ReservaShared.jsx) and wire its `onExpired`
 *     to `handleHoldExpired`, which resets selection + reloads
 *     availability (mirrors `useReservationFlow.handleHoldExpired`).
 *   - 409 (slot taken): surfaces `holdError`, clears the stale selection,
 *     and refetches availability for the current date — same shape as
 *     bar's conflict handling in `proceedWithReservation`, but performed
 *     at the HOLD step (per spec's "Hold conflict" scenario, since the
 *     court backend's overlap check happens at
 *     `POST /reservation/hold`, not only at create).
 *   - 401 during the hold call itself (token expired mid-flow): same
 *     persist + redirect as the no-token case.
 *   - Restore-across-login: on mount, priority 1 restores an unexpired
 *     `court_reservation:hold_state` (no re-hold needed); priority 2
 *     restores `court_reservation:pending_selection` and, if a token is
 *     now present, immediately re-attempts the hold — mirrors
 *     `useReservationFlow`'s two-priority restore effect exactly, scoped
 *     to the court-namespaced keys only.
 *
 * PASS 3 (Unit 3c, THIS UPDATE) adds:
 *   - Buyer form: react-hook-form (`register`/`handleSubmit`/`setValue`/
 *     `trigger`/`errors`), mirroring `useReservationFlow`'s field set
 *     exactly (name/email/phone via `COUNTRIES`-validated country selector/
 *     dni `^\d{7,10}$`/notes/consent — consent is a plain boolean gate, not
 *     an RHF field, same as the bar).
 *   - Checkout: `proceedWithReservation(values, storeId=null)` ->
 *     `createClubReservationApi({clubId, courtId, holdId, date, slotStart,
 *     durationMinutes, name, lastname:"", email, phone, dni, notes,
 *     checkoutOrigin, storeId?}, token)`. `onSubmit` branches on
 *     the selected slot's deposit BEFORE calling it: a slot that charges
 *     online opens the reused
 *     Fiserv card-selection modal (`showPaymentModal`/`modalCard`/
 *     `cardOptions`/`selectedCardOption`/`pendingValues`/
 *     `handleConfirmPaymentSelection`), "free" calls it directly.
 *   - Response branch: `paymentHref` present -> `window.location.href =
 *     resolveUrl(paymentHref)` (Fiserv redirect); otherwise navigate to the
 *     success screen. `reservation:last` (REUSED bar key) is written with
 *     `flow: "canchas"` before either branch, and `barId: clubId` (the
 *     reused field name `ReservaSuccess.jsx` already reads — the backend
 *     stores a club reservation's clubId under the shared `barId` column,
 *     so this works unmodified).
 *   - `skipSaveOnUnmountRef.current = true` is set right after a successful
 *     create, so the now-consumed hold isn't re-persisted to
 *     `court_reservation:hold_state` on unmount (mirrors bar).
 *   - 409 differentiation (deviation from `useReservationFlow.js`, see
 *     inline comment on `proceedWithReservation`): the bar hook
 *     distinguishes "you already booked" vs "this slot conflicted" via
 *     `message.includes("reserva")`, but the court backend's own overlap
 *     message ("El turno se superpone con otra reserva") also contains
 *     that substring, which would misclassify it. Uses
 *     `message.startsWith("Ya ")` instead, which is correct for the
 *     court backend's actual message set (`reservationCheckoutBuilder.js`
 *     `buildPendingCourtReservation`) and satisfies this task's explicit
 *     "409 = slot taken -> back to slot selection + refresh availability"
 *     requirement. 401/402 -> redirect to `/login` (mirrors bar).
 *
 * OPEN MATCH MODE (T10, "Falta un jugador"): `useCourtReservationFlow({ club, mode: "openMatch" })`
 * reuses ALL of the date/sport/duration/availability/slot state above to create an open match instead
 * of a reservation. Differences: (1) no hold — `handleAdvanceToForm` just confirms the slot is picked
 * (POST /open-matches re-checks the slot itself, 409 if taken). Creating requires a logged-in user (T16/T17):
 * `continueOpenMatch()` (the "Continuar" of the rules step) saves the selection plus `matchRules`/`matchCapacity`
 * under `court_reservation:pending_selection` (flag `openMatch: true`) and redirects to /login; after login the
 * restore effect rebuilds the selection and rules and sets `restoredStep = "matchData"` (the data step). A 401
 * from the create call does the same;
 * (2) `matchRules` (alreadyPlayers/kind/visibility/ifNotFull) + `matchCapacity` (players of the court's
 * sport); (3) `publishOpenMatch(values, country, marketingOptIn)` posts the match, stores the manage
 * token (`openMatch:manage:<id>`) and exposes the result as `published`. On 409 it clears the slot and
 * reloads availability, returning "slots" so the layout goes back to Horario. No payment in v1.
 *
 * Generalizes `pages/reservas/useReservationFlow.js`'s state-machine shape:
 * bar's fixed zone+slot becomes variable-duration court+bookableStart.
 *
 * NOTE (deviation from design.md sec.2/sec.4, scoped intentionally): the
 * design describes `ClubDetail.jsx` as calling this hook directly and
 * owning the club fetch. Unit 2 already implemented `ClubDetail.jsx` as a
 * thin shell that fetches the club itself and passes `club`/`courts` down
 * as props to `ClubDetailDesktop`/`ClubDetailMobile` (which already
 * receive `club` as a prop) — this hook therefore takes the already-fetched
 * `club` object as a parameter instead of re-fetching it via `clubId` from
 * `useParams()`. All other behavior matches the design.
 */
export function useCourtReservationFlow({ club, mode = "reservation" }) {
	const isOpenMatch = mode === "openMatch";
	const clubId = club?._id || null;
	const navigate = useNavigate();
	const location = useLocation();
	const token = useSelector((state) => state.auth?.accessToken);
	const authUser = useSelector((state) => state.auth?.user || {});

	// ---- Step 1: date / sport / duration context ----
	// What the club accepts. The court's pricing matrix narrows this further —
	// see `durations` below, derived from the loaded availability.
	const clubDurations =
		Array.isArray(club?.allowedDurations) && club.allowedDurations.length > 0
			? club.allowedDurations
			: ALLOWED_DURATIONS;
	const sports = Array.isArray(club?.sports) ? club.sports : [];

	// Si llega desde el aviso de la lista de espera, abre con el día, el deporte y la duración anotados.
	const waitlistToken = waitlistTokenFromSearch(location.search);
	const wlPrefill = waitlistToken ? waitlistPrefill(location.state) : null;
	const [selectedDate, setSelectedDate] = useState(() => {
		const wanted = wlPrefill?.date ? dayjs(wlPrefill.date, "YYYY-MM-DD", true) : null;
		return wanted && wanted.isValid() && !wanted.isBefore(dayjs().startOf("day")) ? wanted : dayjs().startOf("day");
	});
	const [selectedDuration, setSelectedDuration] = useState(() => {
		const wanted = Number(wlPrefill?.criteria?.durationMinutes);
		return wanted > 0 && clubDurations.includes(wanted) ? wanted : clubDurations[0];
	});
	const [selectedSport, setSelectedSport] = useState(() => {
		const wanted = wlPrefill?.criteria?.sport;
		return wanted && sports.includes(wanted) ? wanted : sports[0] || "";
	});

	const windowDays = Number(club?.reservationWindowDays || 14);
	const minDate = dayjs().startOf("day");
	const maxDate = minDate.add(windowDays, "day");
	const dateLabel = selectedDate.format("YYYY-MM-DD");

	// ---- Step 2: availability + court+start selection ----
	const [availability, setAvailability] = useState({ date: "", courts: [] });
	const [loadingAvail, setLoadingAvail] = useState(false);
	const [availError, setAvailError] = useState("");
	const [hasRequestedAvailability, setHasRequestedAvailability] =
		useState(false);

	// Only the durations some court can actually price. Falls back to the
	// club's list until availability lands, so the tabs never render empty.
	const durations = useMemo(
		() => durationsWithPricing(availability, clubDurations),
		[availability, clubDurations],
	);

	// The selected duration can stop being offered when the buyer switches
	// date or sport. Snap to the first one still on the list instead of
	// leaving the grid silently empty.
	useEffect(() => {
		if (durations.length === 0 || durations.includes(selectedDuration)) return;
		setSelectedDuration(durations[0]);
	}, [durations, selectedDuration]);
	const [selectedCourtId, setSelectedCourtId] = useState(null);
	const [selectedStart, setSelectedStart] = useState(null);

	// ---- Players attending (persisted as the reservation's `partySize`) ----
	//
	// Always sent, whether the buyer touched it or not: the "cada uno paga"
	// split has to be recomputable in Mis Reservas long after checkout, so the
	// number cannot live only in this component's state.
	//
	// `playersEdited` is the checkbox: while false the count tracks the selected
	// court's sport default, so switching from a padel court to a football 5 one
	// re-seeds 4 -> 10. Once the buyer takes ownership of the number we stop
	// overwriting it — silently resetting what someone typed is worse than
	// showing a default that no longer matches the sport.
	const [playersEdited, setPlayersEdited] = useState(false);
	const [playersCount, setPlayersCount] = useState(() =>
		resolveDefaultPlayers(""),
	);

	// Sport of the court the buyer picked, not the sport tab: with no tab
	// selected (`selectedSport` is "") the tab would give us nothing, while the
	// court always knows its own sport.
	const selectedCourtSport = useMemo(() => {
		const courts = Array.isArray(availability.courts) ? availability.courts : [];
		const court = courts.find(
			(c) => c._id === selectedCourtId || c.courtId === selectedCourtId,
		);
		return court?.sport || selectedSport || "";
	}, [availability.courts, selectedCourtId, selectedSport]);

	const defaultPlayers = resolveDefaultPlayers(selectedCourtSport);

	useEffect(() => {
		if (playersEdited) return;
		setPlayersCount(defaultPlayers);
	}, [defaultPlayers, playersEdited]);

	// ---- Open match mode: rules chosen in the "Partido abierto" step ----
	const [rawMatchRules, setRawMatchRules] = useState({
		alreadyPlayers: 1,
		kind: "open",
		visibility: "public",
		ifNotFull: "cancel",
	});
	const [published, setPublished] = useState(null);
	const [createError, setCreateError] = useState("");
	// The creator counts as one player, and at least one spot must stay open.
	const matchCapacity = defaultPlayers;
	const matchRules = {
		...rawMatchRules,
		alreadyPlayers: Math.max(1, Math.min(rawMatchRules.alreadyPlayers, matchCapacity - 1)),
	};
	const setMatchRules = useCallback((patch) => setRawMatchRules((prev) => ({ ...prev, ...patch })), []);

	// ---- Step 3 (Unit 3b): hold + countdown ----
	const [holdId, setHoldId] = useState(null);
	const [holdExpiresAt, setHoldExpiresAt] = useState(null);
	const [holdCourtId, setHoldCourtId] = useState(null);
	const [holdSlotStart, setHoldSlotStart] = useState(null);
	const [holdDateLabel, setHoldDateLabel] = useState(null);
	const [holdLoading, setHoldLoading] = useState(false);
	const [holdError, setHoldError] = useState("");

	// ---- Step 3c: buyer form + checkout ----
	const [submitting, setSubmitting] = useState(false);
	const [submitError, setSubmitError] = useState("");
	const [consent, setConsent] = useState(true);
	// Opt-in to news and promotions: unchecked by default, never required to book.
	const [marketingOptIn, setMarketingOptIn] = useState(false);

	// Fiserv Payment Modal state (mirrors useReservationFlow.js)
	const [showPaymentModal, setShowPaymentModal] = useState(false);
	const [modalCard, setModalCard] = useState("");
	const [cardOptions, setCardOptions] = useState(FISERV_CARD_OPTIONS);
	const [appSettings, setAppSettings] = useState({});
	const [pendingValues, setPendingValues] = useState(null);

	// Country selector state (phone field)
	const [selectedCountry, setSelectedCountry] = useState(COUNTRIES[0]);
	const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
	const countryRef = useRef(null);

	useEffect(() => {
		const handleDocClick = (e) => {
			if (countryRef.current && !countryRef.current.contains(e.target)) {
				setCountryDropdownOpen(false);
			}
		};
		document.addEventListener("mousedown", handleDocClick);
		return () => document.removeEventListener("mousedown", handleDocClick);
	}, []);

	const {
		register,
		handleSubmit,
		setValue,
		trigger,
		formState: { errors },
	} = useForm({
		mode: "onChange",
		defaultValues: {
			name: authUser?.name || "",
			email: authUser?.email || "",
			phone: "",
			dni: authUser?.dni || "",
			notes: "",
		},
	});

	useEffect(() => {
		setValue("name", authUser?.name || "");
		setValue("email", authUser?.email || "");
		setValue("dni", authUser?.dni || "");
	}, [authUser?.name, authUser?.email, authUser?.dni, setValue]);

	useEffect(() => {
		const fetchSettings = async () => {
			try {
				const response = await getAppSettings();
				if (response?.status === 200 && response?.data?.info) {
					setAppSettings(response.data.info);
					const options =
						response.data.info.fiserv_card_options ||
						response.data.info.fiservCardOptions;
					if (Array.isArray(options) && options.length) {
						setCardOptions(options);
					}
				}
			} catch (_) {}
		};
		fetchSettings();
	}, []);

	const selectedCardOption = useMemo(() => {
		return cardOptions.find((opt) => opt.id === modalCard) || null;
	}, [cardOptions, modalCard]);

	useEffect(() => {
		if (typeof document === "undefined") return;
		const bodyEl = document.body;
		if (!bodyEl) return;
		const className = "payment-modal-open";
		if (showPaymentModal) {
			bodyEl.classList.add(className);
		} else {
			bodyEl.classList.remove(className);
		}
		return () => {
			bodyEl.classList.remove(className);
		};
	}, [showPaymentModal]);

	// Hold persistence refs (mirror useReservationFlow.js)
	const holdStateRef = useRef(null);
	const skipSaveOnUnmountRef = useRef(false); // Unit 3c sets this true after a successful checkout, like bar's proceedWithReservation
	const isRestoringRef = useRef(false);
	// Unit 4: guards the Priority-0 preset branch below so a `[clubId, token]`
	// re-run (e.g. token refresh) can't re-consume a stale `presetSelection`
	// still sitting in router state — router state isn't cleared for us by
	// react-router, only by our own `window.history.replaceState` call, so
	// this ref is the single source of truth for "already consumed".
	const presetConsumedRef = useRef(false);

	// restoredStep: 'hold' -> active hold restored (Unit 3c's form/checkout
	// step will jump straight here); 'slots' -> pending selection restored
	// but the hold could not be resumed (no token yet / hold attempt
	// failed); null -> fresh start. Mirrors useReservationFlow's `restoredStep`.
	const [restoredStep, setRestoredStep] = useState(null);

	const loadAvailability = useCallback(
		async (targetDate) => {
			if (!clubId) return;
			const targetDateLabel = targetDate.format("YYYY-MM-DD");
			try {
				setLoadingAvail(true);
				setAvailError("");
				// ADR-6: omit durationMinutes/sport — fetch all durations once,
				// filter client-side (see courtsForSelectedDuration below).
				const response = await getClubAvailabilityApi(clubId, {
					date: targetDateLabel,
				});
				const info = response?.data?.info || {
					date: targetDateLabel,
					courts: [],
				};
				// Unpriced starts never reach the UI: see ./courtDurations.js.
				setAvailability(pruneUnpricedStarts(info));
			} catch (err) {
				setAvailability({ date: targetDateLabel, courts: [] });
				setAvailError(
					err?.response?.data?.message ||
						"No hay disponibilidad para esa fecha.",
				);
			} finally {
				setLoadingAvail(false);
			}
		},
		[clubId],
	);

	// CTA handler ("Reservar turno"): triggers the first fetch. Once
	// active, the effect below refetches on date change only.
	const requestAvailability = useCallback(() => {
		setHasRequestedAvailability(true);
	}, []);

	useEffect(() => {
		if (!hasRequestedAvailability) return;
		loadAvailability(selectedDate);
	}, [selectedDate, hasRequestedAvailability, loadAvailability]);

	// Selection is derived data (court+duration scoped) — clear it whenever
	// date, duration, or sport changes so a stale selection from a previous
	// context can't leak into the hold step. Skipped once (via
	// `isRestoringRef`) right after a restore effect sets date/duration/sport
	// together with a restored selection, so the restore isn't immediately
	// wiped out — mirrors useReservationFlow's dateLabel-change guard.
	useEffect(() => {
		if (isRestoringRef.current) {
			isRestoringRef.current = false;
			return;
		}
		setSelectedCourtId(null);
		setSelectedStart(null);
	}, [selectedDate, selectedDuration, selectedSport]);

	// Per-court bookable starts for the currently selected duration, filtered
	// by the selected sport (if any). This is the sole derivation point the
	// slot UI (`CourtSlotSelector`) reads from — it never re-derives overlap.
	const courtsForSelectedDuration = useMemo(() => {
		const courts = Array.isArray(availability.courts)
			? availability.courts
			: [];
		const filtered = selectedSport
			? courts.filter((c) => c.sport === selectedSport)
			: courts;
		return filtered.map((court) => ({
			...court,
			bookableStarts:
				(court.bookableStarts &&
					court.bookableStarts[String(selectedDuration)]) ||
				[],
		}));
	}, [availability.courts, selectedDuration, selectedSport]);

	const selectSlot = useCallback((courtId, start, duration) => {
		if (duration !== undefined) {
			isRestoringRef.current = true;
			setSelectedDuration(duration);
		}
		setSelectedCourtId(courtId);
		setSelectedStart(start);
	}, []);

	const clearSlotSelection = useCallback(() => {
		setSelectedCourtId(null);
		setSelectedStart(null);
	}, []);

	// ---------------------------------------------------------------------
	// Hold + countdown + auth gate (Unit 3b)
	// ---------------------------------------------------------------------

	// Saves the current selection (plus the open-match rules in that mode) under the court-namespaced key and
	// sends the user to /login; Login returns to `from` and the restore effect picks the selection up.
	const redirectToLogin = useCallback(() => {
		try {
			sessionStorage.setItem(
				COURT_PENDING_SELECTION_KEY,
				JSON.stringify({
					clubId,
					dateLabel,
					selectedDuration,
					selectedSport,
					selectedCourtId,
					selectedStart,
					...(isOpenMatch ? { openMatch: true, matchRules, matchCapacity } : {}),
				}),
			);
		} catch (_) {}
		navigate("/login", {
			state: { from: location.pathname + location.search },
		});
		return { unauthenticated: true };
	}, [clubId, dateLabel, selectedDuration, selectedSport, selectedCourtId, selectedStart, isOpenMatch, matchRules, matchCapacity, navigate, location]);

	// Open match: "Continuar" from the rules step. Creating needs a session, so a guest goes to /login first
	// and comes back to the data step with the rules intact (see `restoredStep = "matchData"`).
	const continueOpenMatch = () => {
		if (!selectedCourtId || !selectedStart) return { ok: false };
		if (!token) return redirectToLogin();
		return { ok: true };
	};

	// Create a hold, auth-gating first. Named `handleAdvanceToForm` to match
	// useReservationFlow.js 1:1 (fidelity target) — Unit 3c's form/checkout
	// step hangs off this same handoff, it just doesn't exist yet.
	const handleAdvanceToForm = useCallback(async () => {
		if (!selectedCourtId || !selectedStart) return;
		// Open match: nothing is held; the create call itself takes (and re-checks) the slot.
		if (isOpenMatch) return { created: true };

		if (!token) return redirectToLogin();

		// Reuse the existing hold if it's still valid for this exact selection.
		const holdStillValid =
			holdId && holdExpiresAt && new Date(holdExpiresAt) > new Date();
		if (
			holdStillValid &&
			holdCourtId === selectedCourtId &&
			holdSlotStart === selectedStart.slotStart &&
			holdDateLabel === dateLabel
		) {
			return { reused: true };
		}

		try {
			setHoldLoading(true);
			setHoldError("");
			const response = await holdClubReservationApi(
				{
					clubId,
					courtId: selectedCourtId,
					date: dateLabel,
					slotStart: selectedStart.slotStart,
					durationMinutes: selectedDuration,
				},
				token,
			);
			const info = response?.data?.info || {};
			setHoldId(info.holdId);
			setHoldExpiresAt(info.expiresAt);
			setHoldCourtId(selectedCourtId);
			setHoldSlotStart(selectedStart.slotStart);
			setHoldDateLabel(dateLabel);
			return { created: true };
		} catch (err) {
			const status = err?.response?.status;
			if (status === 409) {
				setHoldError(
					err?.response?.data?.message ||
						"El turno se ocupó. Elegí otro horario.",
				);
				setSelectedCourtId(null);
				setSelectedStart(null);
				await loadAvailability(selectedDate);
				return { conflict: true };
			}
			if (status === 401) return redirectToLogin();
			setHoldError(
				err?.response?.data?.message ||
					"No pudimos bloquear el turno. Intentá de nuevo.",
			);
			return { error: true };
		} finally {
			setHoldLoading(false);
		}
	}, [
		isOpenMatch,
		selectedCourtId,
		selectedStart,
		token,
		clubId,
		dateLabel,
		selectedDuration,
		selectedSport,
		holdId,
		holdExpiresAt,
		holdCourtId,
		holdSlotStart,
		holdDateLabel,
		redirectToLogin,
		loadAvailability,
		selectedDate,
	]);

	// Hold expired (HoldCountdown's onExpired): reset to slot selection and
	// reload availability, mirroring useReservationFlow.handleHoldExpired.
	const handleHoldExpired = useCallback(() => {
		setHoldId(null);
		setHoldExpiresAt(null);
		setHoldCourtId(null);
		setHoldSlotStart(null);
		setHoldDateLabel(null);
		setSelectedCourtId(null);
		setSelectedStart(null);
		loadAvailability(selectedDate);
		return "slots";
	}, [loadAvailability, selectedDate]);

	// Keep holdStateRef in sync so unmount/beforeunload can persist an active
	// hold without stale closures (mirrors useReservationFlow.js).
	useEffect(() => {
		const holdMatchesCurrent =
			holdId &&
			holdExpiresAt &&
			clubId &&
			holdCourtId === selectedCourtId &&
			holdSlotStart === selectedStart?.slotStart &&
			holdDateLabel === dateLabel;
		if (holdMatchesCurrent) {
			holdStateRef.current = {
				clubId,
				dateLabel,
				selectedDuration,
				selectedSport,
				selectedCourtId,
				selectedStart,
				holdId,
				holdExpiresAt,
			};
		} else {
			holdStateRef.current = null;
		}
	}, [
		holdId,
		holdExpiresAt,
		holdCourtId,
		holdSlotStart,
		holdDateLabel,
		clubId,
		dateLabel,
		selectedDuration,
		selectedSport,
		selectedCourtId,
		selectedStart,
	]);

	// Save active hold on SPA navigation (unmount)
	useEffect(() => {
		return () => {
			if (skipSaveOnUnmountRef.current) return;
			const state = holdStateRef.current;
			if (state && new Date(state.holdExpiresAt) > new Date()) {
				try {
					sessionStorage.setItem(COURT_HOLD_STATE_KEY, JSON.stringify(state));
				} catch (_) {}
			}
		};
	}, []);

	// Save active hold on page refresh / tab close
	useEffect(() => {
		const save = () => {
			if (skipSaveOnUnmountRef.current) return;
			const state = holdStateRef.current;
			if (state && new Date(state.holdExpiresAt) > new Date()) {
				try {
					sessionStorage.setItem(COURT_HOLD_STATE_KEY, JSON.stringify(state));
				} catch (_) {}
			}
		};
		window.addEventListener("beforeunload", save);
		return () => window.removeEventListener("beforeunload", save);
	}, []);

	// Restore hold state after navigation (login redirect or back
	// navigation). ONLY reads/writes the court-namespaced keys — never
	// touches `reservation:hold_state` / `reservation:pending_selection`
	// (ADR-3), so a bar hold in another tab/flow is unaffected.
	useEffect(() => {
		if (!clubId) return;
		try {
			// Priority 0 (Unit 4, design.md Decision 3): a preset entry from
			// QuickSlotModal on the listing page, carried via router state. Wins
			// over both a stored active hold and a stored pending selection — a
			// fresh navigate here is an explicit new intent. Reuses the SAME
			// seeding shape/mechanism as Priority 1 (isRestoringRef single-skip +
			// restoredStep='hold') rather than inventing a parallel one.
			const preset = location.state?.presetSelection;
			if (preset && !presetConsumedRef.current) {
				const isCompletePreset =
					preset.clubId &&
					preset.dateLabel &&
					preset.selectedDuration &&
					preset.selectedCourtId &&
					preset.selectedStart &&
					preset.holdId &&
					preset.holdExpiresAt;

				if (isCompletePreset && String(preset.clubId) === String(clubId)) {
					presetConsumedRef.current = true;
					try {
						sessionStorage.removeItem(COURT_HOLD_STATE_KEY);
						sessionStorage.removeItem(COURT_PENDING_SELECTION_KEY);
					} catch (_) {}
					try {
						window.history.replaceState(
							null,
							"",
							location.pathname + location.search,
						);
					} catch (_) {}

					isRestoringRef.current = true;
					setSelectedDate(dayjs(preset.dateLabel, "YYYY-MM-DD"));
					setSelectedDuration(preset.selectedDuration);
					setSelectedSport(preset.selectedSport || "");
					setSelectedCourtId(preset.selectedCourtId);
					setSelectedStart(preset.selectedStart);
					setHoldId(preset.holdId);
					setHoldExpiresAt(preset.holdExpiresAt);
					setHoldCourtId(preset.selectedCourtId);
					setHoldSlotStart(preset.selectedStart.slotStart);
					setHoldDateLabel(preset.dateLabel);
					setHasRequestedAvailability(true);
					setRestoredStep("hold");
					return;
				}
				// Incomplete/mismatched preset: mark consumed so we don't retry it
				// on a subsequent re-run, but don't advance any step — falls
				// through to Priority 1/2 as if no preset had arrived.
				presetConsumedRef.current = true;
			}

			// Priority 1: active hold -> restore directly, no re-hold needed.
			const rawHold = sessionStorage.getItem(COURT_HOLD_STATE_KEY);
			if (rawHold) {
				const saved = JSON.parse(rawHold);
				if (
					!saved.holdExpiresAt ||
					new Date(saved.holdExpiresAt) <= new Date()
				) {
					sessionStorage.removeItem(COURT_HOLD_STATE_KEY);
				} else if (String(saved.clubId) === String(clubId)) {
					sessionStorage.removeItem(COURT_HOLD_STATE_KEY);
					isRestoringRef.current = true;
					if (saved.dateLabel)
						setSelectedDate(dayjs(saved.dateLabel, "YYYY-MM-DD"));
					if (saved.selectedDuration)
						setSelectedDuration(saved.selectedDuration);
					if (saved.selectedSport !== undefined)
						setSelectedSport(saved.selectedSport || "");
					if (saved.selectedCourtId) setSelectedCourtId(saved.selectedCourtId);
					if (saved.selectedStart) setSelectedStart(saved.selectedStart);
					if (saved.holdId) setHoldId(saved.holdId);
					if (saved.holdExpiresAt) setHoldExpiresAt(saved.holdExpiresAt);
					setHoldCourtId(saved.selectedCourtId || null);
					setHoldSlotStart(saved.selectedStart?.slotStart || null);
					setHoldDateLabel(saved.dateLabel || null);
					setHasRequestedAvailability(true);
					setRestoredStep("hold");
					return;
				}
			}

			// Priority 2: pending selection saved before a login redirect.
			const rawSel = sessionStorage.getItem(COURT_PENDING_SELECTION_KEY);
			if (rawSel) {
				const savedSel = JSON.parse(rawSel);
				// An open-match selection only belongs to the open-match flow (and vice versa).
				if (String(savedSel.clubId) === String(clubId) && Boolean(savedSel.openMatch) === isOpenMatch) {
					sessionStorage.removeItem(COURT_PENDING_SELECTION_KEY);
					isRestoringRef.current = true;
					const restoredDate = savedSel.dateLabel
						? dayjs(savedSel.dateLabel, "YYYY-MM-DD")
						: null;
					if (restoredDate) setSelectedDate(restoredDate);
					if (savedSel.selectedDuration)
						setSelectedDuration(savedSel.selectedDuration);
					if (savedSel.selectedSport !== undefined)
						setSelectedSport(savedSel.selectedSport || "");
					if (savedSel.selectedCourtId)
						setSelectedCourtId(savedSel.selectedCourtId);
					if (savedSel.selectedStart) setSelectedStart(savedSel.selectedStart);
					setHasRequestedAvailability(true);

					if (isOpenMatch) {
						// No hold for open matches: rebuild the rules and land on the data step.
						if (savedSel.matchRules) setRawMatchRules((prev) => ({ ...prev, ...savedSel.matchRules }));
						setRestoredStep(token ? "matchData" : "slots");
					} else if (token) {
						(async () => {
							try {
								setHoldLoading(true);
								setHoldError("");
								const response = await holdClubReservationApi(
									{
										clubId,
										courtId: savedSel.selectedCourtId,
										date: savedSel.dateLabel,
										slotStart: savedSel.selectedStart.slotStart,
										durationMinutes: savedSel.selectedDuration,
									},
									token,
								);
								const info = response?.data?.info || {};
								setHoldId(info.holdId);
								setHoldExpiresAt(info.expiresAt);
								setHoldCourtId(savedSel.selectedCourtId);
								setHoldSlotStart(savedSel.selectedStart.slotStart);
								setHoldDateLabel(savedSel.dateLabel);
								setRestoredStep("hold");
							} catch (err) {
								setHoldError(
									err?.response?.data?.message ||
										"No pudimos bloquear el turno. Intentá de nuevo.",
								);
								setRestoredStep("slots");
							} finally {
								setHoldLoading(false);
							}
						})();
					} else {
						setRestoredStep("slots");
					}
				}
			}
		} catch (_) {}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [clubId, token]);

	// ---------------------------------------------------------------------
	// Buyer form + checkout (Unit 3c)
	// ---------------------------------------------------------------------

	// Create the reservation. `storeId` is only passed for the deposit branch
	// (selected Fiserv card), mirroring useReservationFlow.js's
	// proceedWithReservation 1:1.
	const proceedWithReservation = useCallback(
		async (values, storeId = null) => {
			try {
				setSubmitting(true);
				setSubmitError("");
				// Uses the actually-selected country's prefix (see `selectedCountry`
				// state above, backed by `COUNTRIES` from ReservaShared.jsx) instead
				// of hardcoding "+54" — fixes a bug where non-AR buyers had their
				// number persisted with the wrong dial code regardless of which
				// country they picked in the phone field's country selector.
				let formattedPhone = (values.phone || "").trim();
				if (formattedPhone) {
					const prefix = selectedCountry?.prefix || "+54";
					const prefixDigits = prefix.replace(/\D/g, "");
					formattedPhone = formattedPhone.replace(
						new RegExp(`^(\\+?${prefixDigits}\\s*)`),
						"",
					);
					formattedPhone = `${prefix} ${formattedPhone}`;
				}

				const payload = {
					clubId,
					courtId: selectedCourtId,
					holdId,
					date: dateLabel,
					slotStart: selectedStart.slotStart,
					durationMinutes: selectedDuration,
					name: values.name,
					// design.md sec.4/risk#8: single `name` field reused for form
					// parity with bars; the court backend accepts an optional
					// `lastname` and joins it server-side, so we send it empty.
					lastname: "",
					email: values.email,
					phone: formattedPhone,
					dni: values.dni,
					notes: values.notes,
					// Siempre presente. El backend igual aplicaria el default del
					// deporte si lo omitieramos, pero mandarlo explicito hace que lo
					// guardado sea exactamente el numero que el comprador vio.
					partySize: playersCount,
					checkoutOrigin: window.location.origin,
				};
				if (waitlistToken) payload.waitlistToken = waitlistToken;
				if (marketingOptIn) payload.marketingOptIn = true;
				if (storeId) {
					payload.storeId = storeId;
					payload.numberOfInstallments = 1;
				}
				const response = await createClubReservationApi(payload, token);
				const info = response?.data?.info || {};
				const reservationCode = info?.reservationCode;
				sessionStorage.setItem(
					"reservation:last",
					JSON.stringify({
						flow: "canchas", // ADR-2/ADR-3: REUSED bar key, discriminated by flow
						reservationCode,
						// `ReservaSuccess.jsx` reads `stored.barId` for
						// getReservationStatusApi(barId, code); the backend stores a
						// club reservation's clubId under the shared `barId` field, so
						// this reused key name resolves correctly unmodified.
						barId: clubId,
						courtId: selectedCourtId,
						date: payload.date,
						slotStart: payload.slotStart,
						durationMinutes: selectedDuration,
					}),
				);
				skipSaveOnUnmountRef.current = true;
				sessionStorage.removeItem(COURT_HOLD_STATE_KEY);
				if (info?.paymentHref) {
					window.location.href = resolveUrl(info.paymentHref);
					return;
				}
				const successParams = new URLSearchParams();
				successParams.set("code", String(reservationCode || ""));
				successParams.set("reservationCode", String(reservationCode || ""));
				successParams.set("barId", String(clubId || ""));
				successParams.set("clubId", String(clubId || ""));
				successParams.set("courtId", String(selectedCourtId || ""));
				successParams.set("date", String(payload.date || ""));
				successParams.set("slotStart", String(payload.slotStart || ""));
				successParams.set("durationMinutes", String(selectedDuration || ""));
				if (selectedSport) successParams.set("sport", String(selectedSport));
				navigate(`/reservas/success?${successParams.toString()}`);
			} catch (err) {
				const status = err?.response?.status;
				if (status === 409) {
					const msg = err?.response?.data?.message || "";
					// Court backend's duplicate-booking messages both start with "Ya "
					// ("Ya tenés una reserva..."/"Ya existe una reserva..."); the
					// hold-expired/overlap messages don't. See the file header
					// doc-comment for why this differs from useReservationFlow.js's
					// `.includes("reserva")` heuristic.
					// "seña": el club no puede cobrar señas ahora; reintentar el turno no ayuda.
					if (msg.startsWith("Ya ") || msg.includes("seña")) {
						setSubmitError(msg);
					} else {
						setSubmitError(
							msg ||
								"El bloqueo expiró o el turno se ocupó. Seleccioná nuevamente.",
						);
						setHoldId(null);
						setHoldExpiresAt(null);
						setHoldCourtId(null);
						setHoldSlotStart(null);
						setHoldDateLabel(null);
						setSelectedCourtId(null);
						setSelectedStart(null);
						await loadAvailability(selectedDate);
						return "slots";
					}
				} else if (status === 401 || status === 402) {
					navigate("/login", {
						state: { from: location.pathname + location.search },
					});
				} else {
					setSubmitError(
						err?.response?.data?.message || "No pudimos crear la reserva.",
					);
				}
			} finally {
				setSubmitting(false);
			}
		},
		[
			clubId,
			selectedCourtId,
			playersCount,
			holdId,
			dateLabel,
			selectedStart,
			selectedDuration,
			token,
			navigate,
			location,
			loadAvailability,
			selectedDate,
			selectedCountry,
		],
	);

	// Form submit handler: auth-gates (defensive, token should already be
	// present from handleAdvanceToForm), then branches on the slot's deposit.
	const onSubmit = useCallback(
		async (values) => {
			if (!token) {
				try {
					sessionStorage.setItem(
						COURT_HOLD_STATE_KEY,
						JSON.stringify({
							clubId,
							dateLabel,
							selectedDuration,
							selectedSport,
							selectedCourtId,
							selectedStart,
							holdId,
							holdExpiresAt,
						}),
					);
				} catch (_) {}
				navigate("/login", {
					state: { from: location.pathname + location.search },
				});
				return;
			}

			// The deposit belongs to the winning pricing row, not to the club —
			// see ./courtDeposit.js.
			if (slotRequiresOnlinePayment(selectedStart, club)) {
				setPendingValues(values);
				const defaultCard = cardOptions?.[0]?.id || "";
				setModalCard(defaultCard);
				setShowPaymentModal(true);
				return;
			}

			return await proceedWithReservation(values);
		},
		[
			token,
			clubId,
			dateLabel,
			selectedDuration,
			selectedSport,
			selectedCourtId,
			selectedStart,
			holdId,
			holdExpiresAt,
			navigate,
			location,
			club,
			cardOptions,
			proceedWithReservation,
		],
	);

	const handleConfirmPaymentSelection = useCallback(() => {
		const selectedCard = cardOptions.find((opt) => opt.id === modalCard);
		if (!selectedCard || !selectedCard.storeId) return;
		setShowPaymentModal(false);
		if (pendingValues) {
			proceedWithReservation(pendingValues, selectedCard.storeId);
		}
	}, [cardOptions, modalCard, pendingValues, proceedWithReservation]);

	// Open match: POST the match with the picked slot + rules. See the OPEN MATCH MODE note in the header.
	const publishOpenMatch = useCallback(
		async (values, country, marketingOptIn) => {
			try {
				setSubmitting(true);
				setCreateError("");
				const body = buildCreatePayload(
					{
						clubId,
						courtId: selectedCourtId,
						date: dateLabel,
						slotStart: selectedStart.slotStart,
						durationMinutes: selectedDuration,
						sport: selectedCourtSport,
						...matchRules,
					},
					values,
					country,
					marketingOptIn,
				);
				const response = await createOpenMatchApi(body, token);
				const info = response?.data?.info || {};
				// Creating requires a session, which already authorizes /me, leave and cancel through the bearer.
				// Saving the manage token would leave creator powers in localStorage after logout (T22), so a
				// logged-in creator gets neither a stored token nor a token-bearing "Ver mi partido" link.
				if (token) {
					setPublished({ ...info, manageToken: "" });
				} else {
					saveManageToken(info.matchId || info.match?._id, info.manageToken);
					setPublished(info);
				}
				return "published";
			} catch (err) {
				const { kind, message } = classifyCreateError(err);
				if (kind === "auth") {
					redirectToLogin();
					return "unauthenticated";
				}
				setCreateError(message);
				if (kind === "slot" || kind === "closed") {
					setSelectedCourtId(null);
					setSelectedStart(null);
					await loadAvailability(selectedDate);
					return "slots";
				}
				return "error";
			} finally {
				setSubmitting(false);
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[clubId, selectedCourtId, dateLabel, selectedStart, selectedDuration, selectedCourtSport, rawMatchRules, matchCapacity, token, loadAvailability, selectedDate],
	);

	return {
		waitlistToken,
		// step 1: date / sport / duration context
		durations,
		sports,
		selectedDate,
		setSelectedDate,
		selectedDuration,
		setSelectedDuration,
		selectedSport,
		setSelectedSport,
		minDate,
		maxDate,
		dateLabel,

		// step 2: availability
		availability,
		loadingAvail,
		availError,
		hasRequestedAvailability,
		requestAvailability,
		loadAvailability,
		courtsForSelectedDuration,

		// slot selection
		selectedCourtId,
		selectedStart,
		selectSlot,
		clearSlotSelection,

		// players attending (stored as `partySize`)
		playersCount,
		setPlayersCount,
		playersEdited,
		setPlayersEdited,
		defaultPlayers,
		maxPlayers: MAX_PLAYERS_PER_RESERVATION,

		// step 3: hold + countdown + auth gate
		holdId,
		holdExpiresAt,
		holdLoading,
		holdError,
		handleAdvanceToForm,
		continueOpenMatch,
		handleHoldExpired,
		restoredStep,
		skipSaveOnUnmountRef,

		// step 3c: buyer form + checkout
		register,
		handleSubmit,
		setValue,
		trigger,
		errors,
		submitting,
		submitError,
		consent,
		setConsent,
		marketingOptIn,
		setMarketingOptIn,
		onSubmit,
		selectedCountry,
		setSelectedCountry,
		countryDropdownOpen,
		setCountryDropdownOpen,
		countryRef,
		showPaymentModal,
		setShowPaymentModal,
		modalCard,
		setModalCard,
		cardOptions,
		selectedCardOption,
		pendingValues,
		setPendingValues,
		handleConfirmPaymentSelection,
		appSettings,

		// open match mode
		matchRules,
		setMatchRules,
		matchCapacity,
		publishOpenMatch,
		published,
		createError,
		authUser,
	};
}
